import { type Tag } from '@exam-management/graphql';
import { type QBankStructureLevelItemWithLevel } from '@exam-management/qbs-provider';
import { groupBy } from '@shared/utils';

import type {
  LearningObjectiveStatement,
  ItemSetQuestionPropertiesData,
  QBankStructureDashboardData,
} from '../../types';
import {
  addItemSetQuestionsToQBankStructureTree,
  addLosToQBankStructureTree,
  generateLosRows,
  generateLevelItemRows,
  generateItemSetQuestionRows,
} from '../../utils';

export function formatDataToQBankStructureLosItemSetQuestionDashboardTable(input: {
  qBankStructureLevelItems: QBankStructureLevelItemWithLevel[];
  los: LearningObjectiveStatement[];
  itemSetQuestionPropertiesData: ItemSetQuestionPropertiesData[];
  difficultyTags: Tag[];
  calculationTags: Tag[];
}): QBankStructureDashboardData[] {
  const {
    qBankStructureLevelItems,
    los,
    itemSetQuestionPropertiesData,
    difficultyTags,
    calculationTags,
  } = input;

  const qbsRows = generateLevelItemRows(qBankStructureLevelItems);

  const losRows = generateLosRows(los);

  const losGroupByLevelItemId = groupBy(losRows, 'parentId');

  const qbsTreeWithLos = addLosToQBankStructureTree(qbsRows, losGroupByLevelItemId);

  const difficultyMap = {
    hard: difficultyTags.find((t) => t.name === 'ยาก')?.id ?? '',
    medium: difficultyTags.find((t) => t.name === 'กลาง')?.id ?? '',
    easy: difficultyTags.find((t) => t.name === 'ง่าย')?.id ?? '',
  };

  const calculationMap = {
    calc: calculationTags.find((t) => t.name === 'คำนวณ')?.id ?? '',
    nonCalc: calculationTags.find((t) => t.name === 'ไม่คำนวณ')?.id ?? '',
  };

  const itemSetQuestionRows = generateItemSetQuestionRows(
    itemSetQuestionPropertiesData,
    difficultyMap,
    calculationMap,
  );

  const qbsTreeWithLosAndItemSetQuestions = addItemSetQuestionsToQBankStructureTree(
    qbsTreeWithLos,
    itemSetQuestionRows,
  );

  const childrenByParentId = new Map<string, QBankStructureDashboardData[]>();
  for (const item of qbsTreeWithLosAndItemSetQuestions) {
    if (!item.parentId) continue;
    const children = childrenByParentId.get(item.parentId);
    if (children) {
      children.push(item);
    } else {
      childrenByParentId.set(item.parentId, [item]);
    }
  }

  const cache = new Map<string, { size: number; total: number }>();

  function computeItemSetTotal(id: string): { size: number; total: number } {
    const cached = cache.get(id);
    if (cached) return cached;

    const item = qbsTreeWithLosAndItemSetQuestions.find((i) => i.id === id);
    if (!item) return { size: 0, total: 0 };

    if (item.level === 'item-set-question') {
      const result = { size: 1, total: (item.total ?? 0) * (item.size ?? 0) };
      cache.set(id, result);
      return result;
    }

    const children = childrenByParentId.get(id) ?? [];
    const result = children.reduce(
      (acc, child) => {
        const childValues = computeItemSetTotal(child.id);
        return { size: acc.size + childValues.size, total: acc.total + childValues.total };
      },
      { size: 0, total: 0 },
    );

    cache.set(id, result);
    return result;
  }

  return [...qbsTreeWithLosAndItemSetQuestions].map((item) => {
    if (item.level === 'item-set-question') return item;

    const { size, total } = computeItemSetTotal(item.id);
    return {
      ...item,
      size: size > 0 ? size : undefined,
      total: total > 0 ? total : undefined,
    };
  });
}
